import React, { useRef } from 'react'
import { IProps } from './Interface'
import { Box, MenuItem, Typography } from '@material-ui/core'
import { Input, Select, InputTypeahead } from 'components'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import useStyles from './Styles'
import handleEnterKey from 'utils/handle-enter-key'

const School: React.FC<IProps> = ({
  id,
  year,
  field,
  rules,
  degreeType,
  schoolOptions,
  selectedSchool,
  loadingSearch,
  disabled,
  onSave,
  onChange,
  onSchoolSearch,
  onSchoolRemove,
  onAddNewSchool,
  ...boxProps
}) => {
  const classes = useStyles({})
  const ref = useRef()
  const degreeTypes = rules?.item?.degreeType?.options?.filter(degreeType => degreeType.value) ?? []

  return (
    <Box ref={ref} className={classes.root} {...boxProps}>
      {!disabled && (
        <Box className={classes.highlight}>
          <DeleteOutline
            id="basics-school-remove-button"
            className={classes.removeButton}
            aria-label="remove school"
            onClick={onSchoolRemove}
          />
        </Box>
      )}

      <Box className={classes.row}>
        <Typography id={`basics-input-school-name_${id}-label`} className={classes.label}>
          School
        </Typography>
        <Box width="100%">
          <InputTypeahead
            id={`basics-input-school-name_${id}`}
            disablePortal
            options={schoolOptions || []}
            loading={loadingSearch}
            alwaysEnabled={false}
            defaultValue={selectedSchool || schoolOptions[0]}
            disabled={disabled}
            limit={rules?.item?.schoolName?.maxLength}
            noOptionsText="If your school isn't listed, add it by pressing Enter"
            searchTermAction={onSchoolSearch}
            onChange={option => onChange({ schoolName: option?.label, schoolId: option?.value })}
            onKeyPress={handleEnterKey((value: any) => {
              onAddNewSchool(value)
            })}
            classes={{ paper: classes.schoolListbox }}
            onBlur={() => onSave()}
          />
        </Box>
      </Box>

      <Box className={classes.row}>
        <Typography id={`basics-input-grad-year-${id}-label`} className={classes.label}>
          Graduate year
        </Typography>
        <Input
          id={`basics-input-grad-year-${id}`}
          limit={rules?.item?.gradYear?.maxLength}
          showLimit={true}
          alwaysEnabled={false}
          initialValue={year ? String(year) : ''}
          autoFocus={false}
          onChanged={value => onChange({ gradYear: value })}
          onKeyPress={handleEnterKey((value: any) => {
            onChange({ gradYear: value })
          })}
          onBlur={() => onSave()}
          autoComplete="off"
          disabled={disabled}
        />
      </Box>

      <Box className={classes.row}>
        <Typography id={`basics-degree-type-${id}-label`} className={classes.label}>
          My degree is a
        </Typography>
        <Select
          id={`basics-degree-type-${id}`}
          fullWidth
          variant="outlined"
          alwaysEnabled={false}
          value={degreeType}
          disabled={disabled}
          MenuProps={{
            classes: { list: classes.degreeTypeMenu },
            disablePortal: true,
            disableScrollLock: true
          }}
          onChange={e =>
            onChange({
              degreeType: e.target.value,
              schoolType: degreeTypes.find(option => option.value === e.target.value)?.schoolType
            })
          }
          onKeyPress={handleEnterKey((value: any) => {
            onChange({
              degreeType: value,
              schoolType: degreeTypes.find(option => option.value === value)?.schoolType
            })
          })}
          onBlur={() => onSave()}
        >
          {disabled ? (
            <MenuItem key={degreeType} value={degreeType}>
              {degreeType}
            </MenuItem>
          ) : (
            degreeTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      <Box className={classes.row}>
        <Typography id={`basics-input-field-${id}-label`} className={classes.label}>
          In the field of
        </Typography>
        <Input
          id={`basics-input-field-${id}`}
          limit={rules?.item?.fieldOfStudy?.maxLength}
          showLimit={true}
          alwaysEnabled={false}
          initialValue={field}
          autoFocus={false}
          autoComplete="off"
          disabled={disabled}
          onChanged={value => onChange({ fieldOfStudy: value })}
          onKeyPress={handleEnterKey((value: any) => {
            onChange({ fieldOfStudy: value })
            onSave()
          })}
          onBlur={() => onSave()}
        />
      </Box>
    </Box>
  )
}

School.defaultProps = {
  schoolOptions: [{ value: 'Unknown', label: 'School Name' }],
  year: null,
  field: '',
  rules: {},
  degreeType: '',
  loadingSearch: false,
  disabled: false,
  onSave: () => {},
  onChange: () => {},
  onSchoolSearch: () => {},
  onAddNewSchool: () => {},
  onSchoolRemove: () => {}
}

export default React.memo(School)
